import React from 'react';
import { Lang, CATEGORY_TYPES, EVENTS, ANALYTICS_CONTENT_CATEGORIES_TYPES } from 'shared/constants';
import { Modal, TextInput } from 'src/components/design-system';
import styles from './knowledge-category-editor-popup.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TagsSelector, TAG_SELECTOR_TYPE } from 'src/components/tags/tagSelector';
import { HorizontalDirectoryKnowledgeIcon, HorizontalBinderIcon } from 'src/images';
import { Switch, Radio } from 'antd';
import { LabelsSelector } from 'src/components/labels/labels-selector';
import { ShareToHub } from 'src/components/share-to-hub';
import { useSelector } from 'react-redux';
import {
  isParentSharedToHub,
  getCategoryById,
  isSection,
  hasExternalSyncDescendantById,
} from 'shared/store/selectors/knowledge.selector';
import { isPublicContentProvider } from 'shared/store/selectors/organization.selector';
import analyticsService from 'src/helpers/analytics.service';
import { getCategoryType } from 'shared/utils';
import {
  isExternalSyncReadOnly,
  isPolicyTechSynced,
  buildSyncedUpdatePayload,
} from 'shared/utils/knowledge.utils';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';

const getKnowledgeCategoryTypes = lang => ({
  [CATEGORY_TYPES.CATEGORY]: {
    label: lang?.CATEGORY_TYPE?.[CATEGORY_TYPES.CATEGORY],
  },
  [CATEGORY_TYPES.KNOWLEDGE]: {
    label: lang?.CATEGORY_TYPE?.[CATEGORY_TYPES.KNOWLEDGE],
  },
});

const KnowledgeCategoryEditorPopupComponent = React.memo(
  function KnowledgeCategoryEditorPopupInner({
    open = false,
    lang = Lang.CATEGORY_EDITOR,
    updateCategory = () => {},
    createCategory = () => {},
    onFinish = () => {},
    entity,
  }) {
    entity = entity ?? { id: null, path: null, isSectionCreation: false };
    let isNew = !entity.id;
    let { path, isSectionCreation } = entity;
    let isCategoryCreation = !isNew || !path || isSectionCreation;
    const isCategorySection = isSectionCreation || isSection(path?.split(','));

    const [isLoading, setLoadingState] = React.useState(false);
    const [error, setError] = React.useState(null);

    const isParentShared = useSelector(isParentSharedToHub(entity.path)); // in case of section creation
    const category = useSelector(({ knowledge }) => getCategoryById(knowledge, { id: entity.id }));
    const isOrgPublicContentProvider = useSelector(isPublicContentProvider);

    const isPolicyTechSubtree = useSelector(
      hasExternalSyncDescendantById(entity?.id ? [entity.id] : [], isPolicyTechSynced),
    );

    const isReadOnly =
      !isNew &&
      (isExternalSyncReadOnly(entity?.options) ||
        isExternalSyncReadOnly(category?.options) ||
        isPolicyTechSubtree);
    // `inert` blocks mouse AND keyboard interaction and removes the subtree from the a11y tree.
    const lockedFieldProps = isReadOnly ? { inert: true, style: { opacity: 0.55 } } : {};

    const KNOWLEDGE_CATEGORY_SCHEMA = React.useMemo(
      () =>
        Yup.object().shape({
          title: isReadOnly
            ? Yup.string()
            : Yup.string().min(2, 'Too Short!').max(200, 'Too Long!').required('Required!'),
          subtitle: Yup.string().max(150, 'Too Long!'),
          type: Yup.string().required(),
          ownerIds: isReadOnly
            ? Yup.array().of(Yup.string())
            : Yup.array()
                .of(Yup.string())
                .min(1, 'Please assign at least one owner')
                .required('Required!'),
          specialty: Yup.string().nullable(),
          subspecialty: Yup.string().nullable(),
          contentType: Yup.string().nullable(),
          targetAudience: Yup.array().of(Yup.string()).nullable(),
          hideInWidget: Yup.boolean(),
          // TODO: add the labels as an input
          labels: Yup.array().of(Yup.string()),
          shareToHub: Yup.boolean(),
        }),
      [isReadOnly],
    );

    const updateOrGenerateKnowledge = React.useCallback(
      async values => {
        setLoadingState(true);
        // TODO: create the category
        let categoryRes;
        const {
          specialty,
          subspecialty,
          contentType,
          targetAudience,
          hideInWidget,
          ...valuesData
        } = values;

        const data = {
          ...valuesData,
          tags: { specialty, subspecialty, contentType, targetAudience },
          options: { hideInWidget },
        };

        if (data.shareToHub === undefined) {
          delete data.shareToHub;
        }

        if (isReadOnly) {
          // Synced category: only the user-editable fields go up — the backend enforces
          // the server-owned ones and merges `options`, keeping the provider.
          const readOnlyData = buildSyncedUpdatePayload({
            id: data.id,
            labels: data.labels,
            shareToHub: data.shareToHub,
            hideInWidget,
            specialty,
            subspecialty,
            contentType,
            targetAudience,
          });
          const res = await updateCategory(readOnlyData);
          if (res.error) {
            setError(res.error);
            setLoadingState(false);
            return;
          }

          const EVENT_DATA = {
            categoryType: getCategoryType({ path: values.path, type: values.type }),
            contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.KNOWLEDGE,
            categoryId: values.id,
            title: values.title,
            ownerIds: values.ownerIds,
          };
          analyticsService.track(EVENTS.CATEGORIES.EDIT, EVENT_DATA);
          if (!category?.shared?.isShared && data?.shareToHub) {
            analyticsService.track(EVENTS.KNOWLEDGE_HUB.SHARE_CATEGORY, EVENT_DATA);
          } else if (category?.shared?.isShared && !data?.shareToHub) {
            analyticsService.track(EVENTS.KNOWLEDGE_HUB.UNSHARE_CATEGORY, EVENT_DATA);
          }

          setLoadingState(false);
          onFinish({ ...res, isSectionCreation, isNew });
          return;
        }

        if (isNew) {
          if (isParentShared) {
            data.isParentShared = true;
          }
          categoryRes = await createCategory(data);

          if (categoryRes.error) {
            setError(categoryRes.error);
            setLoadingState(false);
            return;
          }

          if (categoryRes) {
            const EVENT_DATA = {
              categoryType: getCategoryType({
                path: categoryRes.path,
                type: categoryRes.type,
                isSectionCreation,
                isNew: true,
              }),
              contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.KNOWLEDGE,
              categoryId: categoryRes.id,
              title: categoryRes.title,
              ownerIds: categoryRes.ownerIds,
            };

            analyticsService.track(EVENTS.CATEGORIES.ADD, EVENT_DATA);

            if (data?.shareToHub) {
              analyticsService.track(EVENTS.KNOWLEDGE_HUB.SHARE_CATEGORY, EVENT_DATA);
            }
          }
        } else {
          categoryRes = await updateCategory(data);

          if (categoryRes.error) {
            setError(categoryRes.error);
            setLoadingState(false);
            return;
          }

          const EVENT_DATA = {
            categoryType: getCategoryType({ path: values.path, type: values.type }),
            contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.KNOWLEDGE,
            categoryId: values.id,
            title: values.title,
            ownerIds: values.ownerIds,
          };

          analyticsService.track(EVENTS.CATEGORIES.EDIT, EVENT_DATA);

          if (!category?.shared?.isShared && data?.shareToHub) {
            analyticsService.track(EVENTS.KNOWLEDGE_HUB.SHARE_CATEGORY, EVENT_DATA);
          } else if (category?.shared?.isShared && !data?.shareToHub) {
            analyticsService.track(EVENTS.KNOWLEDGE_HUB.UNSHARE_CATEGORY, EVENT_DATA);
          }
        }

        setLoadingState(false);
        onFinish({ ...categoryRes, isSectionCreation, isNew });
      },
      [
        createCategory,
        isNew,
        isSectionCreation,
        onFinish,
        updateCategory,
        isParentShared,
        category,
        isReadOnly,
      ],
    );

    const dismiss = React.useCallback(() => {
      // TODO: analytics

      onFinish({});
    }, [onFinish]);

    const { handleChange, handleSubmit, setFieldValue, values, errors, isValid, dirty } = useFormik(
      {
        enableReinitialize: true,
        validationSchema: KNOWLEDGE_CATEGORY_SCHEMA,
        initialValues: {
          type: isCategoryCreation ? CATEGORY_TYPES.CATEGORY : CATEGORY_TYPES.KNOWLEDGE,
          ...entity.tags,
          ...entity,
          ...entity?.options,
          shareToHub:
            category?.shared?.isShared ||
            ((isParentShared || isOrgPublicContentProvider) && isNew ? true : undefined),
          ownerIds: isNew ? [] : entity.ownerIds || category?.ownerIds || [],
        },
        onSubmit: updateOrGenerateKnowledge,
      },
    );

    const selectCategoryType = React.useCallback(
      e => {
        setFieldValue('type', e.target.value);
      },
      [setFieldValue],
    );

    const resetField = React.useCallback(
      field => {
        // Clear the dependent field through Formik. resetForm() repointed initialValues at the
        // (mutated) current values, which dropped `dirty` and — with enableReinitialize — made the
        // next effect snap the whole form back to the entity, reverting the value the user had just
        // cleared. That made the specialty clear-X a no-op. See BB-17.
        setFieldValue(field, null);
      },
      [setFieldValue],
    );

    const onKeyDown = React.useCallback(e => e.key === 'Enter' && e.preventDefault(), []);

    const onShowOnWidgetToggle = React.useCallback(
      checked => {
        setFieldValue('hideInWidget', !checked);
      },
      [setFieldValue],
    );

    const renderIcon = key => {
      if (key === CATEGORY_TYPES.CATEGORY) {
        return <img src={HorizontalDirectoryKnowledgeIcon} className={styles.icon} alt={key} />;
      }

      return <img src={HorizontalBinderIcon} alt={key} className={styles.icon} />;
    };

    const renderCategoryTypes = () => {
      if (isCategoryCreation) return null;

      return (
        <>
          <label data-key="content-type-label">{lang.SELECT_CATEGORY_TYPE}</label>
          <Radio.Group
            onChange={selectCategoryType}
            value={values.type}
            className={styles.categoriesTypes}
            buttonStyle="solid">
            {Object.keys(getKnowledgeCategoryTypes(lang)).map(key => (
              <Radio.Button key={key} value={key} className={styles.categorySelect}>
                {renderIcon(key)}
                {getKnowledgeCategoryTypes(lang)[key].label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </>
      );
    };

    const labelType = isCategorySection ? CATEGORY_TYPES.SECTION : values.type;
    const isKnowledgeType = values.type === CATEGORY_TYPES.KNOWLEDGE;
    let title = isNew ? lang.CREATE_TITLE : lang.UPDATE_TITLE;

    if (isCategorySection) {
      title = isNew ? lang.CREATE_SECTION_TITLE : lang.UPDATE_SECTION_TITLE;
    }

    return (
      <Modal
        title={title}
        open={open}
        onCancel={dismiss}
        onSubmit={handleSubmit}
        cancelText={lang.DISMISS}
        submitText={isNew ? lang.CREATE : lang.UPDATE}
        loading={isLoading}
        submitDisabled={!isValid || !dirty || isLoading}
        width={600}>
        <form onSubmit={handleSubmit} data-key="content-editor" className={styles.formContent}>
          {renderCategoryTypes()}
          <TextInput
            name="title"
            label={lang.INSERT_CATEGORY_NAME[labelType]}
            value={values.title}
            error={errors.title}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            placeholder={lang.INSERT_CATEGORY_NAME_PLACEHOLDER[labelType]}
            disabled={isReadOnly}
            required
          />
          {isKnowledgeType && (
            <TextInput
              name="subtitle"
              label={lang.DESCRIPTION}
              value={values.subtitle}
              error={errors.subtitle}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              placeholder={lang.DESCRIPTION_PLACEHOLDER}
              disabled={isReadOnly}
            />
          )}

          <div {...lockedFieldProps}>
            <MultipleOwnersSelector
              label={lang.OWNER}
              size="large"
              value={values.ownerIds}
              error={errors.ownerIds}
              placeholder={lang.OWNER_PLACEHOLDER}
              name="ownerIds"
              onChange={setFieldValue}
              borderRadius={4}
              parentOwnerIds={entity?.ownerIds}
            />
          </div>

          {/* Tags are C8-side classification the provider doesn't own, so they stay editable
              on synced categories — the backend merges them per sub-key. */}
          <TagsSelector
            selectedValues={values}
            errors={errors}
            onChange={setFieldValue}
            resetField={resetField}
            type={TAG_SELECTOR_TYPE.KNOWLEDGE}
          />

          <LabelsSelector
            value={values.labels}
            error={errors.labels}
            placeholder={lang.LABELS_PLACEHOLDER}
            name="labels"
            onChange={setFieldValue}
            label={lang.LABELS}
          />

          {isCategorySection && (
            <div className={styles.widgetToggle}>
              <Switch
                checked={!values.hideInWidget}
                onChange={onShowOnWidgetToggle}
                className="ant-switch-secondary"
              />
              <label data-key="content-show-on-widget-label">{lang.SHOW_ON_WIDGET}</label>
            </div>
          )}

          <ShareToHub
            name="shareToHub"
            showHelpdesk
            value={values.shareToHub}
            setFieldValue={setFieldValue}
            topBorder
          />

          {error && <p className={styles.error}>{error}</p>}
          {!isNew && <span className={styles.itemId}>Id: {entity?.id}</span>}
        </form>
      </Modal>
    );
  },
);

export { KnowledgeCategoryEditorPopupComponent };
